import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export default function FirmaordningPage() {
  return (
    <>
      <SEOHead
        title="Firmaordning & Sundhed | Djurs Massage"
        description="Firmaordning og bruttolønsordning for virksomheder på Djursland. Forebyg sygefravær, spændinger og arbejdsskader hos dine medarbejdere."
        ogImage="https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-klinik-ryomgaard.jpg"
      />

      {/* Clean page header - NO container box, NO paragraph above heading */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="max-w-3xl">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1E3A5F] font-serif leading-tight">
            Firmaordning / bruttolønsordning
          </h1>
          <p className="text-slate-700 text-sm sm:text-base mt-3 leading-relaxed font-normal">
            Jeg kan tilbyde en massageordning for firmaer. En investering i medarbejdernes sundhed og velvære, som styrker trivslen og reducerer sygefraværet.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-md transition-shadow"
            >
              <span>Kontakt os for en firmaaftale</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
            <a
              href="tel:28583907"
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-2.5 bg-transparent text-[#1E3A5F] hover:text-black border border-[#1E3A5F] text-xs sm:text-sm font-semibold transition-colors"
            >
              <i className="fa-solid fa-phone text-xs text-[#C9A770]" aria-hidden="true" />
              <span>28 58 39 07</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content directly on page canvas - NO card boxes */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        {/* Model description */}
        <section className="pt-4 border-t border-[#E8E1D5]">
          <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif mb-2">
            Hvordan kan en firmaordning foregå?
          </h2>
          <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-6">
            Det kan foregå på forskellige måder:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2 border-l-2 border-[#1E3A5F] pl-4 py-1">
              <h3 className="font-bold text-base sm:text-lg text-[#1E3A5F] font-serif">1. På virksomheden</h3>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                Jeg kommer ud på virksomheden og udfører massage (min. 4 timer per gang). Medbringer selv massagebriks og professionelt udstyr.
              </p>
            </div>

            <div className="space-y-2 border-l-2 border-[#1E3A5F] pl-4 py-1">
              <h3 className="font-bold text-base sm:text-lg text-[#1E3A5F] font-serif">2. Hos Djurs Massage (Bruttoløn)</h3>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                Massagen foregår hos Djurs Massage og trækkes i lønnen hos medarbejderen over den såkaldte Bruttolønsordning - dette skal gå igennem firmaet, som en abonnementsordning.
              </p>
            </div>

            <div className="space-y-2 border-l-2 border-[#C9A770] pl-4 py-1 bg-amber-50/30">
              <h3 className="font-bold text-base sm:text-lg text-[#1E3A5F] font-serif">3. Klippekort til medarbejdere</h3>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                Virksomheden har mulighed for at købe fleksible klippekort til Djurs Massage, som frit kan benyttes og deles af medarbejderne efter behov. En uforpligtende og populær løsning.
              </p>
            </div>
          </div>
        </section>

        {/* Fordele Grid directly on page canvas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-6 border-t border-[#E8E1D5]">
          {/* Fordele for virksomheden */}
          <div className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
              Fordele for virksomheden
            </h2>
            <ul className="space-y-3 text-xs sm:text-sm text-slate-700 pt-1">
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Ingen ekstra lønudgift</strong> – medarbejderen betaler selv via løntræk
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Lavere sygefravær</strong> pga. bedre velvære og forebyggelse af skader
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Styrket virksomhedsprofil</strong> – attraktivt personalegode
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Enkel administration</strong> – kan håndteres via lønsystemet
                </div>
              </li>
            </ul>
          </div>

          {/* Fordele for medarbejderen */}
          <div className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-[#1E3A5F] font-serif">
              Fordele for medarbejderen
            </h2>
            <ul className="space-y-3 text-xs sm:text-sm text-slate-700 pt-1">
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Betaler med før-skat penge</strong> → op til 30–40 % besparelse
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Mindre spændinger, stress og smerter</strong> i en travl arbejdsdag
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Nemmere at prioritere velvære</strong>, når det er tilgængeligt på arbejdspladsen
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#C9A770] mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <strong className="text-[#1E3A5F]">Fast aftale</strong> giver regelmæssig og effektiv behandling
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Callout directly on page - No webform */}
        <section className="pt-8 border-t border-[#E8E1D5]">
          <div className="max-w-2xl space-y-3">
            <h3 className="text-lg sm:text-xl font-bold text-[#1E3A5F] font-serif">
              Vil du høre mere om mulighederne for din virksomhed?
            </h3>
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              Vi tager gerne en uforpligtende dialog om, hvordan en massageordning bedst kan strikkes sammen til netop jeres behov og arbejdstider.
            </p>
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-[#1E3A5F] text-white text-xs font-semibold rounded-full hover:shadow-md transition-shadow"
              >
                <span>Gå til kontaktsiden</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
              <a
                href="mailto:djursmassage@gmail.com"
                className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 bg-white border border-[#D8CDBE] text-[#1E3A5F] text-xs font-semibold hover:shadow-xs transition-shadow"
              >
                <i className="fa-solid fa-envelope text-[#C9A770]" aria-hidden="true" />
                <span>djursmassage@gmail.com</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
